import { useEffect, JSX } from 'react'
import React from 'react'

import {
  type CLSize,
  DISABLED_INPUT_CLASSNAME,
  Tooltip,
} from '~/components/component-library'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { ClearButton } from '~/components/inputs/ClearButton'
import { cn } from '~/utils/tailwind-utils'

// Chrome and Safari deliberately ignore autocomplete="off" for profile-based
// autofill (mailing addresses, contacts, cards) and classify fields by their
// name/label/placeholder — so an Ethereum "address" field gets the native
// mailing-address popup. An unrecognized token is the one value that actually
// suppresses it. Keep "off" as the default: Firefox honors it, and swapping it
// globally would re-enable Firefox's heuristics on every field.
export const AUTOCOMPLETE_SUPPRESS = 'do-not-autofill'

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'prefix'
> {
  message?: string
  error?: string
  autoFocus?: boolean
  value?: string
  displayMessage?: string
  autoComplete?: string
  disabledReason?: string
  disabled?: boolean
  asComponent?: () => JSX.Element
  onClearInput?: () => void
  clearButtonClassName?: string
  clearEventName?: string
  actionIcon?: JSX.Element
  prefix?: JSX.Element
  unit?: string
  isLoading?: boolean
  disableWhenLoading?: boolean
  backgroundShadow?: boolean
  focusRing?: boolean
  prefixPadding?: boolean
  rounded?: boolean
  messageClassName?: string
  messagePositionClassName?: string
  containerClassName?: string
  showError?: boolean
  inputSize?: CLSize
}

export const Input = ({
  ref,
  name,
  placeholder,
  onChange,
  onBlur,
  autoFocus,
  error,
  value,
  type = 'text',
  autoComplete = 'off',
  disabledReason = '',
  disabled = false,
  asComponent,
  onClearInput,
  clearButtonClassName,
  clearEventName,
  actionIcon,
  prefix,
  onFocus,
  onKeyDown,
  onMouseUp,
  inputMode,
  className,
  unit,
  isLoading = false,
  disableWhenLoading = true,
  backgroundShadow = true,
  focusRing = true,
  prefixPadding = true,
  rounded = true,
  messageClassName,
  messagePositionClassName,
  maxLength,
  min,
  max,
  step,
  id,
  containerClassName,
  showError = true,
  inputSize = 'base',
}: InputProps & {
  ref?: React.Ref<HTMLInputElement | null>
}): JSX.Element => {
  const inputElementRef = React.useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (inputElementRef.current) {
      if (autoFocus) inputElementRef.current.focus()
    }
  }, [autoFocus])

  const isSmall = inputSize === 'small'
  const Component = asComponent || 'input'
  const autofillDisabled =
    autoComplete === 'off' || autoComplete === AUTOCOMPLETE_SUPPRESS
  return (
    <div className="w-full">
      <div
        className={cn(
          'relative w-full flex-grow outline-none transition',
          rounded && 'rounded-lg',
          backgroundShadow && [
            'ring-1 ring-inset',
            !error && 'ring-gray-200 dark:ring-gray-700',
            !error && 'bg-white dark:bg-gray-900',
            error && 'bg-white ring-red-500 dark:bg-gray-900 dark:ring-red-500',
          ],
          focusRing &&
            !error &&
            !disabled &&
            'focus-within:ring-2 focus-within:ring-blue-500',
          focusRing && error && 'focus-within:ring-2 focus-within:ring-red-500',
          disabled && DISABLED_INPUT_CLASSNAME,
          containerClassName,
        )}
      >
        {/* Inlines its own always-mounted Tooltip rather than the (conditional)
            DisabledReasonTooltip the typed inputs use — keeping the wrapper
            stable so toggling `disabled` never remounts the field. */}
        <Tooltip
          content={disabledReason}
          isDisabled={!disabled || !disabledReason}
        >
          <div className="relative flex w-full items-center justify-between gap-px">
            {prefix && (
              <div
                className={cn('shrink-0', {
                  [isSmall ? 'py-1.5 pl-3 pr-1.5' : 'py-2.5 pl-3.5 pr-2']:
                    prefixPadding,
                })}
              >
                {prefix}
              </div>
            )}
            <div className="relative min-w-0 shrink grow overflow-hidden">
              <Component
                id={id}
                className={cn(
                  'flex w-full items-center space-x-2 truncate bg-transparent placeholder:text-placeholder focus:outline-none',
                  isSmall ? 'py-1.5 text-sm' : 'py-2.5 text-base',
                  prefix
                    ? isSmall
                      ? 'pl-0 pr-3'
                      : 'pl-0 pr-3.5'
                    : isSmall
                      ? 'px-3'
                      : 'px-3.5',
                  {
                    'text-primary': !error,
                    'text-error': error,
                    'opacity-60': isLoading,
                    'cursor-not-allowed': disabled,
                    // Drop the right padding when a trailing element follows
                    // (the inline unit and/or clear button) so the value runs
                    // up to it instead of truncating ~14px early. The unit and
                    // clear button supply their own spacing.
                    '!pr-0': !!onClearInput || !!unit,
                  },
                  className,
                )}
                placeholder={placeholder}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                type={type}
                disabled={disabled || (disableWhenLoading && isLoading)}
                name={name}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
                onMouseUp={onMouseUp}
                inputMode={inputMode}
                maxLength={maxLength}
                min={min}
                max={max}
                step={step}
                ref={ref}
                // NOTE: These are to prevent password managers from adding UI to the input
                data-1p-ignore={autofillDisabled}
                data-lpignore={autofillDisabled}
                data-bwignore={autofillDisabled}
                data-form-type={autofillDisabled ? 'other' : undefined}
              />
            </div>
            {unit && (
              <div
                className={cn(
                  'pointer-events-none shrink-0 select-none whitespace-nowrap text-secondary',
                  isSmall ? 'pl-1.5 text-sm' : 'pl-2 text-base',
                  // when no clear button follows, the unit is the last element,
                  // so it needs its own right padding to clear the border
                  { [isSmall ? 'pr-3' : 'pr-3.5']: !onClearInput },
                )}
              >
                {unit}
              </div>
            )}
            {isLoading ? (
              <div
                className={cn(
                  'text-secondary opacity-60',
                  isSmall ? 'px-3' : 'px-3.5',
                )}
              >
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              actionIcon
            )}

            {onClearInput && (
              <ClearButton
                onClick={onClearInput}
                eventName={clearEventName}
                eventProperties={{
                  inputName: name,
                }}
                hasError={!!error}
                className={cn(clearButtonClassName)}
                inputSize={inputSize}
              />
            )}
          </div>
        </Tooltip>
      </div>
      {error && showError && (
        <InputMessage
          isError={!!error}
          message={error}
          className={messageClassName}
          positionClassName={messagePositionClassName}
        />
      )}
    </div>
  )
}
Input.displayName = 'Input'

export const InputMessage = ({
  message,
  isError,
  className,
  positionClassName,
}: {
  message?: string
  isError?: boolean
  className?: string
  positionClassName?: string
}) => {
  return message ? (
    <div
      className={cn(
        'mt-1.5 flex select-none items-center text-xs',
        positionClassName,
      )}
    >
      <p
        className={cn(
          {
            'text-error': isError,
            'text-green-500 dark:text-green-400': !isError,
          },
          className,
        )}
      >
        {message}
      </p>
    </div>
  ) : (
    <div />
  )
}
